'use client'
import { pricingGoogsQueryOption } from "@/apis/query/pricing"
import { ArrowUpRight } from "@/ui/icons/ArrowUpRight"
import { PerkImageChat } from "@/ui/icons/PerkImageChat"
import { PerkMemory } from "@/ui/icons/PerkMemory"
import { PerkNoWatermark } from "@/ui/icons/PerkNoWatermark"
import { PerkPremiumChat } from "@/ui/icons/PerkPremiumChat"
import { PerkItem, PerkPromptModal } from "@/ui/PerkPromptModal"
import { Button, ModalProps, useDisclosure } from "@heroui/react"
import { useQuery } from "@tanstack/react-query"
import { useLocale, useTranslations } from "next-intl"
import PayMethodModal from "./PayMethodModal"
import { findMonthlyPremiumGoodsId } from "./findMonthlyPremiumGoods"
import { useContinuePayment } from "./useContinuePayment"

const PERKS = [
  { Icon: PerkMemory, labelKey: "pricing.perkRestoreLongMemory" },
  { Icon: PerkImageChat, labelKey: "pricing.perkMoreChatImages" },
  { Icon: PerkNoWatermark, labelKey: "pricing.perkNoWatermarkDownloads" },
  { Icon: PerkPremiumChat, labelKey: "pricing.perkPremiumChat" },
] as const

export default ({
  onViewPlans,
  ...props
}: Omit<ModalProps, "children"> & {
  onViewPlans: () => void
}) => {
  const t = useTranslations()
  const locale = useLocale()
  const { subscribeByGoodsId, isLoading, dialogs } = useContinuePayment()
  const payMethodModal = useDisclosure()
  const { data, isPending } = useQuery(pricingGoogsQueryOption(1))
  const goodsId = findMonthlyPremiumGoodsId(data?.data?.goods)

  const startSubscribe = (via: "paypal" | "payssion" = "paypal") => {
    if (!goodsId) {
      onViewPlans()
      return
    }
    subscribeByGoodsId(goodsId, via)
  }

  return (
    <>
      <PerkPromptModal
        title={t("pricing.vipExpireTitle")}
        subTitle={t("pricing.vipExpireDesc")}
        perks={PERKS.map(({ Icon, labelKey }) => (
          <PerkItem key={labelKey} icon={Icon}>{t(labelKey)}</PerkItem>
        ))}
        {...props}
      >
        {(onClose) => (
          <>
            <Button
              fullWidth
              radius="full"
              isLoading={isLoading || isPending}
              className="mt-6 h-11 bg-gradient-to-l from-gradient-purple2 to-gradient-blue2 text-[16px] font-medium tracking-[-0.3px] text-white"
              onPress={() => {
                if (!goodsId) {
                  onClose()
                  onViewPlans()
                  return
                }
                if (locale === "ru") {
                  payMethodModal.onOpen()
                  return
                }
                startSubscribe("paypal")
              }}
            >
              {t("pricing.upgradeToUnlock")}
            </Button>

            <button
              type="button"
              className="mt-4 flex w-full items-center justify-center gap-0.5 text-[12px] font-medium leading-4 tracking-[-0.3px] text-black/70"
              onClick={() => {
                onClose()
                onViewPlans()
              }}
            >
              {t("pricing.viewAllPlans")}
              <ArrowUpRight width={16} height={16} className="size-4 opacity-70" />
            </button>
          </>
        )}
      </PerkPromptModal>

      <PayMethodModal
        isOpen={payMethodModal.isOpen}
        onOpenChange={payMethodModal.onOpenChange}
        isPaypalLoading={isLoading}
        isPayssionLoading={isLoading}
        onPressPaypal={() => startSubscribe("paypal")}
        onPressPayssion={() => startSubscribe("payssion")}
      />
      {dialogs}
    </>
  )
}
